import React, { useState } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface FeatureItem {
  title: string;
  description: string;
  tooltip?: string;
}

const FeatureCard: React.FC<{ 
  title: string; 
  description: string;
  tooltip?: string;
}> = ({
  title,
  description,
  tooltip
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="bg-[rgba(214,255,235,1)] p-4 rounded-lg w-[180px]">
      <div className="flex items-center justify-between">
        <h3 className="text-[rgba(0,63,40,1)] text-[20px] font-bold font-slate">{title}</h3>
        {tooltip && (
          <TooltipProvider delayDuration={0}>
            <Tooltip open={isOpen}>
              <TooltipTrigger asChild>
                <button 
                  className="p-1 rounded-full hover:bg-[rgba(0,63,40,0.1)]"
                  onClick={handleClick}
                >
                  <Info className="w-4 h-4 text-[rgba(0,63,40,1)]" />
                </button>
              </TooltipTrigger>
              <TooltipContent onPointerDownOutside={() => setIsOpen(false)}>
                <p className="text-sm">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <p className="text-[rgba(0,63,40,1)] text-[16px] mt-1">{description}</p>
    </div>
  );
};

interface FeatureGridProps {
  initialFeatures?: FeatureItem[];
  additionalFeatures?: FeatureItem[];
}

export const FeatureGrid: React.FC<FeatureGridProps> = ({
  initialFeatures = [
    { title: "Weight", description: "1 Kg"},
    { title: "Season", description: "Baishak-Jestha"},
    { title: "Harvested by", description: "Satashi Mahila", tooltip: "Local cooperative of women beekeepers" },
    { title: "Bee Type", description: "Apis mellifera", tooltip: "European honey bee species" },
  ],
  additionalFeatures = [
    { title: "Processing", description: "Cold Pressed", tooltip: "Extracted without heat" },
    { title: "Location", description: "Chitwan, Nepal" },
    { title: "Certification", description: "ISO 22000:2018", tooltip: "" },
    { title: "Best Before", description: "2 Years", tooltip: "Shelf life from packaging" },
  ]
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const features = isExpanded ? [...initialFeatures, ...additionalFeatures] : initialFeatures;

  return (
    <div className="flex flex-col items-center">
      <div className="grid grid-cols-2 gap-4 px-4 mt-14 max-w-[440px] mx-auto">
        {features.map((feature, index) => (
          <div 
            key={index}
            className="transition-all duration-200 ease-in-out"
          >
            <FeatureCard {...feature} />
          </div>
        ))}
      </div>
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-8 px-6 py-2 bg-[rgba(0,63,40,1)] text-white rounded-full font-slate text-base hover:bg-[rgba(0,63,40,0.9)] flex items-center gap-2"
      >
        {isExpanded ? "Show Less" : "Know More"}
        <svg 
          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>
  );
};
